import type { Page } from '../../types.js';

/**
 * The page to hand a compliance officer.
 *
 * It leads with what is *structural* rather than what is promised, because that is
 * the distinction a reviewer is actually testing: an isolation claim backed by
 * separate credentials is a different object from one backed by a policy. The
 * unfinished items are named in the body rather than buried at the end, on the
 * principle that they are cheaper as an opening statement than as a finding.
 */
const page: Page = {
  slug: 'security-and-trust',
  title: 'Security and trust',
  lede:
    'Your customers stay yours: we never see a password and never receive a name. Each bank gets its own database with its own key. The controls below are structural — they are properties of the design rather than rules somebody has to remember.',
  group: 'Assurance',
  blocks: [
    {
      kind: 'stats',
      items: [
        { value: '1:1', label: 'Databases to banks', sub: 'Plus a key used for nothing else', accent: 'violet' },
        { value: '0', label: 'Personal data fields received', sub: 'You send a reference you invented', accent: 'violet' },
        { value: '15 min', label: 'Default idle session', sub: 'Twelve hours absolute', accent: 'violet' },
        { value: '2', label: 'People per sensitive change', sub: 'The approver cannot be the requester', accent: 'teal' },
      ],
    },

    { kind: 'heading', text: 'What we hold and what we never hold' },
    {
      kind: 'split',
      left: {
        title: 'We hold',
        body: [
          {
            kind: 'list',
            items: [
              'A reference to a customer that you invented and that means nothing outside your systems.',
              'That reference\'s holdings, quotes, orders and ledger entries.',
              'Your configuration: brand, theme, locale, commissions, limits and feature switches.',
              'Your registered web addresses, app identifiers and public keys.',
            ],
          },
        ],
      },
      right: {
        title: 'We never hold',
        body: [
          {
            kind: 'list',
            items: [
              'A customer password. There is no login of ours for them to create.',
              'A name, email address, phone number, account number or identity document.',
              'Any private key of yours — we store only the public half.',
              'Another bank\'s records in the same place as yours.',
            ],
          },
        ],
      },
    },
    {
      kind: 'prose',
      text: 'There is nowhere in the integration for personal data to be sent. Your team could not put a customer name into it by accident, because nothing asks for one. That is worth saying to a reviewer who expects a promise and finds a structure.',
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'What this does to a data-protection assessment',
      text: 'The assessment covers a reference and a set of trades rather than a customer record. A right-to-be-forgotten request is satisfied inside your systems: break the link between the reference and the person and we hold nothing that identifies anybody. Cross-border questions get simpler for the same reason.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'One database per bank' },
    {
      kind: 'split',
      left: {
        title: 'The usual design',
        body: [
          {
            kind: 'prose',
            text: 'One shared database with a column naming which bank each row belongs to.',
          },
          {
            kind: 'list',
            items: [
              'Isolation depends on every query remembering to filter by that column.',
              'One forgotten filter is a cross-bank disclosure.',
              'A restore or a residency requirement touches every bank at once.',
              'An auditor cannot be given direct read access to anything.',
            ],
          },
        ],
      },
      right: {
        title: 'What we do instead',
        body: [
          {
            kind: 'prose',
            text: 'A separate database per bank, with credentials that can physically open only that one.',
          },
          {
            kind: 'list',
            items: [
              'There is no bank column inside a vault, because it would be a constant.',
              'The filter that could be forgotten does not exist, so the bug class is unwritable.',
              'Restores, residency and backup schedules are per bank.',
              'A month-end batch in one bank is invisible to every other.',
            ],
          },
        ],
      },
    },
    {
      kind: 'prose',
      text: 'Which bank a request belongs to is read from the verified credential and from nowhere else — never from something in the request. There is no value a caller could send that would widen what they can reach.',
    },

    { kind: 'heading', text: 'Onboarding creates a vault; offboarding destroys the key first' },
    {
      kind: 'flow',
      accent: 'violet',
      steps: [
        { title: 'Catalogue entry', sub: 'Marked provisioning' },
        { title: 'Key created', sub: 'Scoped to you alone' },
        { title: 'Role and database', sub: 'Credentials for one vault' },
        { title: 'Schema applied', sub: 'Version recorded' },
        { title: 'Credential wrapped', sub: 'Only a reference is stored' },
        { title: 'Marked active', sub: 'Traffic may now route' },
      ],
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Erasure is a key operation, not a delete script',
      text: 'Offboarding destroys the encryption key before it drops the database. Every backup taken before that moment becomes ciphertext with no key in existence. Until the sequence completes, requests for that bank are refused rather than served from a half-built vault.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What every request has to prove' },
    {
      kind: 'table',
      columns: ['Check', 'What it stops'],
      rows: [
        ['The credential is genuine, unexpired and was issued for this door', 'Forged tokens, and tokens minted for a different part of the platform'],
        ['The request is signed by the private key the credential was bound to', '**Theft.** A copied credential without that key is inert'],
        ['The signature covers this exact method and address', 'A proof captured on a cheap read being replayed against an expensive write'],
        ['The signature is fresh and has not been seen before', 'Replay, including two copies racing each other'],
        ['The request comes from a web address or an app you registered', 'Your page being copied onto somebody else\'s site with a working trade screen in it'],
        ['The session is still live on our side', 'Use after revocation, logout or deprovisioning — it bites on the next call'],
        ['The permission covers this operation, and high-value ones re-check how recently the customer proved who they are', 'A six-hour-old session approving a sale'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'The private key never leaves the device',
      text: 'On a browser it is generated in a form the page itself cannot read out, so injected script cannot steal it. On a phone it lives in the secure hardware and the app receives signatures rather than the key. There is no configuration that turns this off.',
    },
    {
      kind: 'prose',
      text: 'Renewal routes through your backend rather than straight to us, so your logout ends a customer session without you calling us at all. You can also end one immediately, and your logout should.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Change control and audit' },
    {
      kind: 'ladder',
      steps: [
        {
          title: 'One operator proposes',
          text: 'The change is validated as it is typed, so an impossible commission or a limit that contradicts another is refused at the keyboard rather than three days later on a live quote.',
        },
        {
          title: 'A second operator reviews and approves',
          text: 'The database enforces that the approver is not the requester, and an approval that sits too long expires rather than lingering. Confidential figures are withheld from the queue without hiding that a change is being made.',
        },
        {
          title: 'The change is applied as a separate step',
          text: 'A failed apply leaves an approved, unapplied record rather than a half-changed configuration, so the state is recoverable and visible.',
        },
        {
          title: 'The trail is written where it cannot be quietly edited',
          text: 'Who did what, to which bank, with the request identifier that ties the entry to the response that came back. Your own vault also carries a local trail that can be handed to an auditor directly.',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      title: 'Reconciliation is a query your auditor can run',
      text: 'Every order writes entries that sum to zero in both metal and cash. An auditor can be handed that check and a read credential on your own vault, and run it themselves. A shared-database design could never offer that.',
    },

    { kind: 'heading', text: 'Device and app assurance on mobile' },
    {
      kind: 'table',
      columns: ['Platform', 'Key custody', 'App check', 'What you set up'],
      rows: [
        ['Web', 'Browser key store, non-extractable', 'None — the registered web address is the control', 'Your address list, with us'],
        ['iOS', 'Secure Enclave', 'App Attest', 'An Apple developer organisation account'],
        ['Android with Play Services', 'Android Keystore, hardware-backed where available', 'Play Integrity', 'A Google Play developer account and two keys from its console'],
        ['Huawei without Play Services', 'Huawei hardware key store', 'Safety Detect', 'An AppGallery Connect account and its own signing fingerprint'],
      ],
    },
    {
      kind: 'callout',
      tone: 'stop',
      title: 'An unconfigured device check refuses; it does not wave requests through',
      text: 'Each app-check provider needs credentials from its vendor. Until those are in place the check fails closed and says exactly what is missing. There is no stub that reports success, and no silent downgrade to a weaker key if hardware is unavailable.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What to raise in your first review, not your second' },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Three items a reviewer will find, so hear them from us',
      text: 'Key management runs on our own envelope implementation rather than a cloud hardware security service. The behaviour a reviewer cares about — a separate key per bank, and destroying the key destroying the data — is real today; the hardware backing is planned.',
    },
    {
      kind: 'list',
      items: [
        '**Operator sign-in is not finished.** The administrative surface authenticates with a shared development token, and it refuses to serve that authentication in a production configuration at all. Proper operator sign-on for named people is planned and not built, so administrative changes go through us under the two-person rule described above.',
        '**Infrastructure has not been exercised end to end.** The database schema, the provisioning sequence and the shredding path are written, reviewed and unit-tested, but the build environment has no live database, so those paths have not been run against real infrastructure. Ask for the current position when you scope the review; it moves.',
        '**Errors are deliberately vague to a caller.** A failure at the token endpoint never distinguishes an unknown client from a bad signature, because that difference is an oracle. The real reason is in our log and in the audit trail, not in the response.',
      ],
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What stays on your side of the line' },
    {
      kind: 'table',
      columns: ['Your responsibility', 'Why it matters', 'Who owns it'],
      rows: [
        [
          'Decide who the customer is from your own login, never from the app',
          'This is the one integration mistake that lets a logged-in customer read another customer\'s holdings',
          'Your engineers, checked at code review',
        ],
        [
          'Keep our credential in your secret store, never in an app or a web page',
          'It is what proves a request comes from your bank rather than an imitation of it',
          'Your platform or security team',
        ],
        [
          'Keep the list of addresses and app identifiers accurate',
          'It is what stops a copy of your page hosting a working trade screen elsewhere',
          'Your team, with us',
        ],
        ['End our session when you end yours', 'Keeps one logout meaning one logout', 'Your engineers'],
      ],
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Operations',
          text: 'Who can see and change what once you are live, and what your own systems receive.',
          href: '/business/operations',
          accent: 'teal',
        },
        {
          title: 'Rollout',
          text: 'Where the security and compliance reviews sit in the path from decision to launch.',
          href: '/business/rollout',
          accent: 'teal',
        },
        {
          title: 'Surfaces',
          text: 'The per-platform differences in key custody and app checks, side by side.',
          href: '/business/surfaces',
          accent: 'blue',
        },
      ],
    },
  ],
};

export default page;
